import { Link } from "react-router-dom";
import { Star, ChevronRight, MapPin } from "lucide-react";
import { photoUrl } from "../../lib/api";
import { priceLabel, CATEGORY_LABEL } from "../../lib/constants";

const Photo = ({ item, c, badge, className, style }) => (
  <div className={`relative ${className}`} style={style} data-testid="featured-image-wrap">
    {(item.photos || []).length ? (
      <img src={photoUrl(item.photos[0])} alt={item.title} className="absolute inset-0 w-full h-full object-cover" />
    ) : (
      <div className="absolute inset-0 flex items-center justify-center" style={{ background: c.panel_color }}>
        <Star size={64} style={{ color: c.accent_color, opacity: 0.3 }} />
      </div>
    )}
    {badge && (
      <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full" style={{ background: c.accent_color, color: c.button_text }} data-testid="featured-badge">
        <Star size={12} style={{ fill: c.button_text }} /> {badge}
      </span>
    )}
  </div>
);

const Body = ({ item, c, t, compact, tight, onClick }) => (
  <div className={`flex flex-col justify-center overflow-hidden ${compact ? "p-3 sm:p-4 flex-1 min-w-0" : tight ? "p-5 sm:p-6 md:pr-16" : "p-5 sm:p-6 md:p-10 md:pr-16"}`} style={{ color: c.text_color }}>
    {c.show_category && <span className="text-xs font-semibold uppercase tracking-wide shrink-0" style={{ color: c.accent_color }} data-testid="featured-category">{CATEGORY_LABEL[item.category] || item.category}</span>}
    <h3 className={`font-display font-extrabold mt-1.5 line-clamp-2 leading-[1.2] pb-0.5 shrink-0 ${compact ? "text-sm sm:text-base" : tight ? "text-xl sm:text-2xl" : "text-xl sm:text-2xl md:text-3xl"}`}>{item.title}</h3>
    {c.show_description && <p className={`mt-2 opacity-70 min-h-0 shrink overflow-hidden ${compact || tight ? "text-xs sm:text-sm line-clamp-2" : "text-sm md:text-base line-clamp-2 md:line-clamp-3"}`} data-testid="featured-description">{item.description || "A featured listing from the CritterPosts community."}</p>}
    {(c.show_price || c.show_location) && (
      <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 shrink-0 ${compact ? "text-xs" : "text-sm"} opacity-90`}>
        {c.show_price && <span className={`font-bold ${compact ? "text-sm" : "text-base sm:text-lg"}`} style={{ color: c.accent_color }} data-testid="featured-price">{item.lf_type ? item.lf_type.toUpperCase() : priceLabel(item)}</span>}
        {c.show_location && <span className="flex items-center gap-1 truncate" data-testid="featured-location"><MapPin size={13} /> {item.city}, {item.province}</span>}
      </div>
    )}
    {t.button && (
      <Link to={`/listing/${item.id}`} onClick={onClick} data-testid={`featured-cta-${item.id}`} style={{ background: c.button_bg, color: c.button_text }}
        className={`inline-flex w-fit items-center gap-1.5 font-semibold rounded-xl transition-[filter] hover:brightness-110 shrink-0 ${compact ? "mt-2.5 h-8 px-3 text-xs" : tight ? "mt-3 px-5 h-10" : "mt-4 sm:mt-6 px-5 sm:px-6 h-10 sm:h-11"}`}>
        {t.button} <ChevronRight size={compact ? 15 : 18} />
      </Link>
    )}
  </div>
);

export const BigSlide = ({ item, c, t, hm, hd, onClick, mode = "auto" }) => {
  const grid = mode === "mobile" ? "grid-cols-1" : mode === "desktop" ? "grid-cols-[var(--fs)_1fr] h-[var(--fd)]" : "md:grid-cols-[var(--fs)_1fr] md:h-[var(--fd)]";
  const h = mode === "mobile" ? "h-[var(--fm)]" : mode === "desktop" ? "h-full" : "h-[var(--fm)] md:h-full";
  const order = c.photo_side === "right" ? (mode === "desktop" ? "order-2" : mode === "mobile" ? "" : "md:order-2") : "";
  const split = c.photo_split || 50;
  return (
    <div className={`grid ${grid}`} style={{ "--fm": hm, "--fd": hd, "--fs": `${c.photo_side === "right" ? 100 - split : split}%` }}>
      <Photo item={item} c={c} badge={t.badge} className={`${h} ${order}`} />
      <Body item={item} c={c} t={t} tight={parseInt(hd, 10) < 330} onClick={onClick} />
    </div>
  );
};

export const SmallCard = ({ item, c, t, hd, onClick }) => (
  <div className="flex flex-row h-[var(--fd)] rounded-xl overflow-hidden border border-white/10" style={{ background: c.panel_color, "--fd": hd }} data-testid={`featured-card-${item.id}`}>
    <Photo item={item} c={c} badge={t.badge} className="h-full shrink-0" style={{ width: `${c.photo_split || 50}%` }} />
    <Body item={item} c={c} t={t} compact onClick={onClick} />
  </div>
);
